import { usePublishSearchViewport } from 'components/InteractiveSearch/SearchViewportContext';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { get } from 'lodash';
import {
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Typography,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';
import * as uiActions from 'modules/UI/actions';
import Page from 'components/Page';
import InteractiveSearch from 'components/InteractiveSearch';
import ImageSrc from 'components/Logo/LogoFull.png';
import TopOffers from './components/TopOffers';
import Offers from 'components/offers/Offers';
import { AuthPopupEnum } from 'utils/constants';
import api from 'utils/api';
import { useLocation } from 'utils/location';
import useOfferDisplayMode from 'components/offers/useOfferDisplayMode';
import { offerFilters, getOfferFilterFromSearchParams, applyOfferFilterToSearchParams } from 'components/InteractiveSearch/offerFilterParams';

const LandingPage = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('lg'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayMode] = useOfferDisplayMode(searchParams.get('display'));
  const dispatch = useDispatch();
  const userLocation = useLocation();
  
  // State for snackbar notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Filter state - only used for InteractiveSearch now, not for Popular Offers
  const selectedFilter = getOfferFilterFromSearchParams(searchParams);
  const setSelectedFilter = (filter) => setSearchParams(applyOfferFilterToSearchParams(searchParams, filter));
  const [mapViewport, setMapViewport] = useState(null);
  usePublishSearchViewport(mapViewport);

  // Only pass radius to search API when user explicitly set it in URL; otherwise backend
  // uses per-offer service_radius_miles (e.g. 15 miles set by the business).
  const radiusParam = searchParams.get('radius');
  const radiusQuery = radiusParam ? `&radius=${encodeURIComponent(radiusParam)}` : '';

  // Fetch nearby offers for landing-page interactive search
  const nearbyOffersUrl = userLocation
    ? `/offer/search?query=&latitude=${get(userLocation, 'latitude') || ''}&longitude=${get(userLocation, 'longitude') || ''}&start=0&rows=50&local=true${radiusQuery}&expireSoon=false`
    : null;

  const { data: nearbyOffersResponse } = useSWR(
    nearbyOffersUrl,
    () => api.get(nearbyOffersUrl),
    { revalidateOnFocus: false }
  );

  const nearbyOffers = get(nearbyOffersResponse, 'content', []);

  const mapOffersUrl = mapViewport && userLocation
    ? `/offer/search?query=&latitude=${get(userLocation, 'latitude') || ''}`
      + `&longitude=${get(userLocation, 'longitude') || ''}&start=0&rows=50&local=true`
      + `&mapLatitude=${mapViewport.latitude}&mapLongitude=${mapViewport.longitude}`
      + `&mapRadius=${mapViewport.radius}&expireSoon=${selectedFilter === offerFilters.expireSoon}`
      + (selectedFilter === offerFilters.nearMe ? '&sort=distance' : '')
    : null;

  const { data: mapOffersResponse } = useSWR(
    mapOffersUrl,
    () => api.get(mapOffersUrl),
    { revalidateOnFocus: false },
  );
  const mapOffers = mapOffersResponse ? get(mapOffersResponse, 'content', []) : undefined;

  // Local offer and Near me share eligibility; only Near me changes the ranking.
  const isLocalFilter = selectedFilter === offerFilters.local
    || selectedFilter === offerFilters.nearMe;
  const localOffersUrl = isLocalFilter && userLocation
    ? `/offer/search?query=&latitude=${get(userLocation, 'latitude') || ''}&longitude=${get(userLocation, 'longitude') || ''}&start=0&rows=50&local=true${radiusQuery}&expireSoon=false`
      + (selectedFilter === offerFilters.nearMe ? '&sort=distance' : '')
    : null;

  const { data: localOffersResponse, isValidating: isValidatingLocalOffers } = useSWR(
    localOffersUrl,
    () => api.get(localOffersUrl),
    { revalidateOnFocus: false }
  );

  const localOffers = get(localOffersResponse, 'content', []);
  const isLoadingLocalOffers = !localOffersResponse && isValidatingLocalOffers;

  // Fetch expiring soon offers when expireSoon filter is selected
  const expiringSoonOffersUrl = selectedFilter === offerFilters.expireSoon && userLocation
    ? `/offer/search?query=&latitude=${get(userLocation, 'latitude') || ''}&longitude=${get(userLocation, 'longitude') || ''}&start=0&rows=50&local=false&expireSoon=true`
    : null;

  const { data: expiringSoonOffersResponse, isValidating: isValidatingExpiringSoonOffers } = useSWR(
    expiringSoonOffersUrl,
    () => api.get(expiringSoonOffersUrl),
    { revalidateOnFocus: false }
  );

  const expiringSoonOffers = get(expiringSoonOffersResponse, 'content', []);
  const isLoadingExpiringSoonOffers = !expiringSoonOffersResponse && isValidatingExpiringSoonOffers;

  const interactiveOffers = isLocalFilter
    ? localOffers
    : selectedFilter === offerFilters.expireSoon
      ? expiringSoonOffers
      : nearbyOffers;

  // Fetch top offers for "Popular offers" strip
  const topOffersUrl = userLocation
    ? `/offer/top10clicks?latitude=${get(userLocation, 'latitude')}&longitude=${get(userLocation, 'longitude')}`
    : null;
  
  const { data: topOffers } = useSWR(
    topOffersUrl, 
    () => api.get(topOffersUrl), 
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Handle email verification redirect
    const verification = searchParams.get('verification');
    
    // Handle Facebook email not found error
    const error = searchParams.get('error');
    if (error === 'FACEBOOK_EMAIL_NOT_FOUND') {
      setSnackbar({
        open: true,
        message: 'No email associated to the FB account. Please create an account with an email',
        severity: 'error'
      });
      // Clean up URL parameter
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      if (newParams.toString()) {
        setSearchParams(newParams);
      } else {
        setSearchParams(new URLSearchParams());
      }
      return;
    }

    if (verification) {
      // Open auth drawer with login form
      //dispatch(uiActions.showLogin());
      dispatch(uiActions.showLogin(AuthPopupEnum.login))

      // Show appropriate message
      if (verification === 'success') {
        setSnackbar({
          open: true,
          message: 'Email verified successfully! You can now log in.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Email verification failed. Please try again.',
          severity: 'error'
        });
      }

      // Clean up URL parameters
      // const newParams = new URLSearchParams(searchParams);
      // newParams.delete('verification');
      // setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams, dispatch]);

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Page>
      <Helmet>
        <title>HiddenUnderGround - Unearth Great Deals</title>
      </Helmet>
      
      {/* Snackbar for verification messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Container maxWidth="xl" sx={{ width: "100%" }}>
        <Box
          mb={6}
          mt={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <img
            src={ImageSrc}
            alt="HiddenUnderGround logo"
            height={matches ? 60 : 80}
          />
        </Box>

        {/* Interactive Search Interface */}
        <Box mb={6} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
          <Box sx={{ width: '100%' }}>
            <InteractiveSearch 
              offers={interactiveOffers}
              mapOffers={mapOffers}
              userLocation={userLocation}
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
              onMapViewportChange={setMapViewport}
            />
          </Box>
        </Box>

        {/* Local offer and Near me share the same eligible offers with different ranking. */}
        {isLocalFilter && (
          <Box my={6}>
            <Box mb={4}>
              <Typography variant="h4" gutterBottom>
                {selectedFilter === offerFilters.nearMe ? 'Near me' : 'Local offers'}
              </Typography>
            </Box>
            {isLoadingLocalOffers ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : localOffers.length > 0 ? (
              <Offers 
                offers={localOffers} 
                offerOrigin="localOffers" 
                offerType={displayMode}
                columns={3}
              />
            ) : (
              <Typography variant="body1" color="text.secondary">
                {selectedFilter === offerFilters.nearMe
                  ? 'No offers found near you.'
                  : 'No local offers for this region.'}
              </Typography>
            )}
          </Box>
        )}

        {/* Expiring Soon Offers Section - shown when expireSoon filter is selected */}
        {selectedFilter === offerFilters.expireSoon && (
          <Box my={6}>
            <Box mb={4}>
              <Typography variant="h4" gutterBottom>
                Offers expiring soon
              </Typography>
            </Box>
            {isLoadingExpiringSoonOffers ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : expiringSoonOffers.length > 0 ? (
              <Offers 
                offers={expiringSoonOffers} 
                offerOrigin="expiringSoonOffers" 
                offerType={displayMode}
                columns={3}
              />
            ) : (
              <Typography variant="body1" color="text.secondary">
                No expiring soon offers found.
              </Typography>
            )}
          </Box>
        )}

        <Box my={6}>
          <Box mb={4}>
            <Typography variant="h4" gutterBottom>
              Popular offers
            </Typography>
          </Box>
          <TopOffers />
        </Box>
      </Container>
    </Page>
  );
};

export default LandingPage;
